import type {
  AmendBookingInput,
  AmendmentOperationInput,
  Booking,
  BookingGarment,
  GarmentState,
} from '../api/bookings';
import { formatAmount } from '../format/money';
import { netRental, resolveAll } from './discount';
import { priceGarment, type UnitRateCard } from './pricing';
import type { AvailableUnit } from './rail';
import { liveGarments } from './discountForm';
import { withReference } from '../units/referenceNumber';

/**
 * A change being assembled against a booking that already exists.
 *
 * <p>
 * In the shape `draft.ts`, `intake.ts` and `rail.ts` established: plain data, pure functions over
 * it, and nothing that touches the server. That is what lets the delta move in front of the clerk
 * while the customer is still deciding.
 * </p>
 *
 * <p>
 * <strong>It holds the operations, not a rewritten party.</strong> That is what the endpoint wants
 * anyway — a whole-tree replace would make the server guess whether a missing garment was removed by
 * this clerk or added by a colleague ten seconds ago — and it buys the thing the counter needs most:
 * the pending changes are renderable as a list, each with its own figure, *before* anything is
 * posted.
 * </p>
 *
 * <p>
 * <strong>`amendmentProblems` mirrors the server's two gates, with the cost `garmentMoves.ts` names
 * about itself:</strong> a rule added in C# and not here is one the screen quietly stops enforcing.
 * The alternative — letting 400s teach the clerk — is the more expensive failure, because a counter
 * that has learned the screen lies stops reading it.
 * </p>
 */

/** A garment coming in, carrying enough to price it and to name it back. */
export interface IncomingGarment {
  unitId: number;
  serialCode: string;
  /** The shop's own number, shown "RN-7" beside the serial; never sent. */
  referenceNumber?: string | null;
  itemId: number;
  itemName: string;
  /** Resolved against the booking's *existing* charged period — an amendment does not move dates. */
  rate: number | null;
  rateCard: UnitRateCard | null;
}

/** Somebody joining the party, with whatever they are wearing. */
export interface NewWearer {
  /** Client-side only, until the server assigns one. */
  key: string;
  name: string;
  relation: string;
  mobile: string;
  garments: IncomingGarment[];
}

export interface Amendment {
  /** Garment ids coming off the booking. */
  removals: number[];
  /** `garmentId` is the line going out, `incoming` is what takes its place. */
  swaps: { garmentId: number; incoming: IncomingGarment }[];
  /** Onto somebody already in the party. */
  additions: { wearerId: number; incoming: IncomingGarment }[];
  newWearers: NewWearer[];
  reason: string;
  /** Set once the clerk has been shown the shortfall and gone ahead anyway. */
  acknowledgeShortfall: boolean;
}

export function emptyAmendment(): Amendment {
  return {
    removals: [],
    swaps: [],
    additions: [],
    newWearers: [],
    reason: '',
    acknowledgeShortfall: false,
  };
}

let nextKey = 0;

/** Keys only have to be unique within one screen's lifetime, which a counter makes cheap. */
export function newWearerKey(): string {
  nextKey += 1;

  return `n${nextKey}`;
}

/* ----------------------------------------------------------------- the gates */

/**
 * Whether a garment can be taken off the booking from where it is — mirroring
 * `BookingService.CanRemove`.
 *
 * <p>
 * The line is drawn at leaving the shop. Everything before that is a rail movement, and putting one
 * back is what a shop does all day; once it is `Issued`, taking it off the booking would leave a
 * garment out in the world that nothing is accountable for.
 * </p>
 */
export function canRemove(state: GarmentState): boolean {
  return (
    state === 'Pending' ||
    state === 'Allocated' ||
    state === 'Fitting' ||
    state === 'Alteration' ||
    state === 'Ready'
  );
}

/**
 * Whether a garment can be swapped from where it is — everything {@link canRemove} allows, plus
 * `Issued`, which is the pickup-day exchange this feature exists for.
 */
export function canSwap(state: GarmentState): boolean {
  return canRemove(state) || state === 'Issued';
}

/** Whether this booking can be amended at all — mirroring the server's stage gate. */
export function canAmend(booking: Booking): boolean {
  return (
    booking.stage === 'Enquiry' ||
    booking.stage === 'Quotation' ||
    booking.stage === 'Hold' ||
    booking.stage === 'Confirmed' ||
    booking.stage === 'InProgress'
  );
}

/** Every garment the booking is actually for, wearer by wearer. */
// Shared with the discount form and the phone.
export { liveGarments };

/**
 * What the rail says while a replacement for `garment` is being picked: who it goes to, and what
 * the swap keeps and leaves.
 *
 * <p>
 * <strong>It must not promise what taking a booking's swap promises.</strong> There, the old
 * garment's accessories follow it; here the server prices a fresh line and the accessories stay on
 * the old one, which a clerk would otherwise only discover when the kalgi is missing from the
 * hand-over. So they are named whenever there are any.
 * </p>
 */
export function swapWords(
  booking: Booking,
  garment: BookingGarment,
): { forName: string | null; sentence: string } {
  const forName =
    booking.wearers.find((wearer) => wearer.garments.some((row) => row.id === garment.id))?.name ??
    null;
  const leaves = garment.accessories.map((row) => row.name);

  return {
    forName,
    sentence: [
      `Whatever is picked next goes in place of ${garment.serialCode}${forName ? ` for ${forName}` : ''}.`,
      'The old line stays on the booking as history and the customer pays the difference.',
      leaves.length > 0 ? `What went with it (${leaves.join(', ')}) stays on the old line.` : '',
    ]
      .filter(Boolean)
      .join(' '),
  };
}

/* ------------------------------------------------------------------- pricing */

export interface PendingChange {
  /** Stable enough to key a row on, and to remove the change by. */
  key: string;
  kind: 'add' | 'remove' | 'swap';
  what: string;
  /** Who it is for, so a party of four reads as four people rather than eleven garments. */
  who: string;
  /** Signed: negative when the customer is owed. Null when nothing prices the incoming garment. */
  delta: number | null;
}

/**
 * The pending changes as a clerk reads them out, each with its own figure.
 *
 * <p>
 * A list rather than a total, because "you are dropping the safa and taking the cream sherwani
 * instead" is the sentence, and a single net number is the one thing a customer will not accept
 * without it being broken down.
 * </p>
 */
export function pendingChanges(booking: Booking, amendment: Amendment): PendingChange[] {
  const changes: PendingChange[] = [];

  const byId = new Map<number, { garment: BookingGarment; wearer: string }>();

  for (const wearer of booking.wearers) {
    for (const garment of wearer.garments) {
      byId.set(garment.id, { garment, wearer: wearer.name });
    }
  }

  for (const garmentId of amendment.removals) {
    const found = byId.get(garmentId);

    changes.push({
      key: `remove-${garmentId}`,
      kind: 'remove',
      what: found
        ? `${found.garment.itemName} · ${withReference(found.garment.serialCode, found.garment.referenceNumber)}`
        : 'Garment',
      who: found?.wearer ?? '',

      // The line comes off the total in full. Any retention the shop keeps is a charge in its own
      // right — a payment row or a deposit deduction — and never a rate left standing on a garment
      // the customer does not receive, which would put it on the invoice as something delivered.
      delta: -(found?.garment.rentalRate ?? 0),
    });
  }

  for (const swap of amendment.swaps) {
    const found = byId.get(swap.garmentId);
    const out = found?.garment.rentalRate ?? 0;

    changes.push({
      key: `swap-${swap.garmentId}`,
      kind: 'swap',
      what: found
        ? `${withReference(found.garment.serialCode, found.garment.referenceNumber)} → ${withReference(swap.incoming.serialCode, swap.incoming.referenceNumber)}`
        : withReference(swap.incoming.serialCode, swap.incoming.referenceNumber),
      who: found?.wearer ?? '',
      delta: swap.incoming.rate === null ? null : swap.incoming.rate - out,
    });
  }

  for (const addition of amendment.additions) {
    const wearer = booking.wearers.find((row) => row.id === addition.wearerId);

    changes.push({
      key: `add-${addition.incoming.unitId}`,
      kind: 'add',
      what: `${addition.incoming.itemName} · ${withReference(addition.incoming.serialCode, addition.incoming.referenceNumber)}`,
      who: wearer?.name ?? '',
      delta: addition.incoming.rate,
    });
  }

  for (const wearer of amendment.newWearers) {
    for (const garment of wearer.garments) {
      changes.push({
        key: `new-${wearer.key}-${garment.unitId}`,
        kind: 'add',
        what: `${garment.itemName} · ${withReference(garment.serialCode, garment.referenceNumber)}`,
        who: wearer.name || 'New wearer',
        delta: garment.rate,
      });
    }
  }

  return changes;
}

export interface AmendmentMoney {
  /** Gross, both ends. The concession is reported separately because it moves with the party. */
  rentalBefore: number;
  rentalAfter: number;
  /** The gross change. **Not what the customer pays more** when a percentage discount applies. */
  delta: number;
  discountBefore: number;
  discountAfter: number;
  /**
   * What the customer actually pays more or less, once the concession has moved with them.
   *
   * <p>
   * <strong>This is the sentence a clerk reads out, and it is not {@link delta}.</strong> A booking
   * at 10% off that gains a ₹4,000 sherwani costs ₹3,600 more — a percentage discount is a rule
   * about the price and follows the party, while a flat sum was a figure somebody agreed to and
   * stays where it is.
   * </p>
   */
  payableDelta: number;
  /** What the shop physically holds. Zero until a deposit payment has been recorded. */
  depositHeld: number;
  depositSuggested: number;
  /** Positive when the shop should collect more; negative when it is holding too much. */
  depositShortfall: number;
  /** Whether the shortfall is one the server will refuse without an acknowledgement. */
  needsAcknowledgement: boolean;
}

/**
 * What the change does to the money — <strong>a preview, and deliberately so.</strong>
 *
 * <p>
 * The server re-sums from the lines inside the transaction and its answer is the one that is
 * charged. This exists so the figure moves as the clerk picks, which is the whole argument for the
 * screen being a page rather than a drawer; `pricing.ts` documents the same split about itself.
 * </p>
 */
export function amendmentMoney(booking: Booking, amendment: Amendment): AmendmentMoney {
  const rentalBefore = booking.rentalAmount ?? 0;

  const delta = pendingChanges(booking, amendment).reduce(
    (running, change) => running + (change.delta ?? 0),
    0,
  );

  const rentalAfter = Math.max(0, rentalBefore + delta);

  // Re-resolved exactly as the server does on the way out: a percentage follows the new rental, a
  // flat sum is only re-clamped in case the party shrank below it. Neither re-checks the ceiling —
  // the concession was authorised when it was given, and the clerk amending the party may not be
  // the person who granted it.
  const discountBefore = booking.discount.totalAmount;

  // A garment's own concession travels with the garment: a removed line takes its away, a swapped
  // one goes with the line it replaces, and an added one arrives with none. Only the booking share
  // is re-resolved, against the new post-line subtotal.
  const surviving = liveGarments(booking)
    .filter((garment) => !amendment.removals.includes(garment.id))
    .filter((garment) => !amendment.swaps.some((swap) => swap.garmentId === garment.id))
    .map((garment) => ({
      key: garment.id,
      rate: garment.rentalRate ?? 0,
      input: { pct: garment.discountPct, amount: garment.discountAmount },
    }));

  // Incoming garments carry no concession of their own, but they do change the subtotal the booking
  // share is computed over — so they are in the basis at full rate, as one undiscounted line.
  const incomingRate = rentalAfter - surviving.reduce((sum, line) => sum + line.rate, 0);

  const discountAfter = resolveAll(
    incomingRate > 0
      ? [...surviving, { key: -1, rate: incomingRate, input: { pct: null, amount: null } }]
      : surviving,
    booking.discount.pct !== null
      ? { pct: booking.discount.pct, amount: null }
      : { pct: null, amount: booking.discount.amount },
  ).total;

  // What the shop has, not what the booking says it should have. NotTaken means nothing has moved,
  // so there is nothing that can disagree with the cash box and the figure is simply recomputed.
  const held =
    booking.deposit.state === 'Held' || booking.deposit.state === 'PartRefunded'
      ? (booking.deposit.amount ?? 0)
      : 0;

  // An overridden deposit suggests itself. A typed figure is a decision, and warning about it on
  // every amendment of every booking that ever had one is how a warning stops being read.
  const overridden = booking.deposit.overrideReason !== null;

  const suggested = overridden
    ? (booking.deposit.amount ?? 0)
    : round(rentalAfter * (booking.deposit.multiplier ?? 1.5));

  const shortfall = held > 0 ? suggested - held : 0;

  return {
    rentalBefore,
    rentalAfter,
    delta,
    discountBefore,
    discountAfter,
    payableDelta: round(
      netRental(rentalAfter, discountAfter) - netRental(rentalBefore, discountBefore),
    ),
    depositHeld: held,
    depositSuggested: suggested,
    depositShortfall: shortfall,

    // **Only when *this* change raises the figure**, mirroring the server. A booking can already be
    // under-secured when the screen opens — somebody amended it upwards earlier and the top-up has
    // not been collected — and gating on the standing gap would demand an acknowledgement for every
    // later change, including the ones that reduce it. The shortfall is still reported either way,
    // because it is true either way; what changes is whether the clerk has to answer for it.
    needsAcknowledgement: shortfall > 0 && rentalAfter > rentalBefore,
  };
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/* ------------------------------------------------------------------ problems */

export interface AmendmentProblem {
  message: string;
}

/**
 * Everything standing between this amendment and the server accepting it.
 *
 * A list rather than a boolean, for the reason `draftProblems` gives: disabling the commit button
 * with no explanation is the version of this that generates support calls.
 */
export function amendmentProblems(booking: Booking, amendment: Amendment): AmendmentProblem[] {
  const problems: AmendmentProblem[] = [];

  if (!canAmend(booking)) {
    problems.push({
      message: `A booking at ${booking.stage} cannot be amended. Take a new booking instead.`,
    });

    return problems;
  }

  const changes = pendingChanges(booking, amendment);

  if (changes.length === 0) {
    problems.push({ message: 'Nothing has changed yet.' });
  }

  if (!amendment.reason.trim()) {
    problems.push({
      message: 'Say why the garments are changing — this is what the shop reads back in a dispute.',
    });
  }

  const byId = new Map(liveGarments(booking).map((garment) => [garment.id, garment]));

  for (const garmentId of amendment.removals) {
    const garment = byId.get(garmentId);

    if (!garment) {
      problems.push({ message: 'One of the garments being dropped is no longer on the booking.' });
      continue;
    }

    if (!canRemove(garment.state)) {
      problems.push({
        message: `${garment.serialCode} cannot be taken off the booking from ${garment.state}.`,
      });
    }
  }

  for (const swap of amendment.swaps) {
    const garment = byId.get(swap.garmentId);

    if (!garment) {
      problems.push({ message: 'One of the garments being swapped is no longer on the booking.' });
      continue;
    }

    if (!canSwap(garment.state)) {
      problems.push({
        message: `${garment.serialCode} cannot be swapped from ${garment.state}.`,
      });
    }
  }

  // Unfinished setup, not a freebie. The server refuses these outright rather than summing them as
  // zero, so saying nothing here would mean a clerk pressing Apply and getting a 400 about a rate
  // card in the middle of a conversation with a customer.
  const unpriced = changes.filter((change) => change.delta === null);

  if (unpriced.length > 0) {
    problems.push({
      message: `${unpriced.length} incoming garment${unpriced.length === 1 ? ' has' : 's have'} no rate card. Publish a rate for ${unpriced.length === 1 ? 'it' : 'them'} first.`,
    });
  }

  // A booking with nothing on it is a cancellation, and it has to go through the stage endpoint so
  // it takes a reason and lands where the no-show analysis reads. Said here rather than left to the
  // server, because the server can only say it after applying and rolling back.
  if (remainingGarmentCount(booking, amendment) === 0) {
    problems.push({
      message:
        'That would leave the booking with no garments. Cancel the booking instead, so it is recorded as a cancellation.',
    });
  }

  for (const wearer of amendment.newWearers) {
    if (!wearer.name.trim()) {
      problems.push({ message: 'Every new wearer needs a name.' });
      break;
    }
  }

  const money = amendmentMoney(booking, amendment);

  if (money.needsAcknowledgement && !amendment.acknowledgeShortfall) {
    problems.push({
      message:
        'The deposit already held no longer covers this booking. Collect the difference, or tick the acknowledgement.',
    });
  }

  return problems;
}

/**
 * How many garments the booking would be left with.
 *
 * Swaps are deliberately absent from the arithmetic: one goes out and one comes in, so the count
 * does not move. Counting a swap as a removal would make swapping the only garment on a booking read
 * as emptying it, and the screen would refuse the most ordinary amendment there is.
 */
export function remainingGarmentCount(booking: Booking, amendment: Amendment): number {
  const added =
    amendment.additions.length +
    amendment.newWearers.reduce((running, wearer) => running + wearer.garments.length, 0);

  return liveGarments(booking).length - amendment.removals.length + added;
}

/* -------------------------------------------------------------------- state */

export function toggleRemoval(amendment: Amendment, garmentId: number): Amendment {
  return amendment.removals.includes(garmentId)
    ? { ...amendment, removals: amendment.removals.filter((id) => id !== garmentId) }
    : {
        ...amendment,
        removals: [...amendment.removals, garmentId],

        // A garment cannot be both dropped and swapped. Picking one clears the other rather than
        // refusing, because the clerk has just said which they meant.
        swaps: amendment.swaps.filter((swap) => swap.garmentId !== garmentId),
      };
}

export function setSwap(
  amendment: Amendment,
  garmentId: number,
  incoming: IncomingGarment,
): Amendment {
  return {
    ...amendment,
    removals: amendment.removals.filter((id) => id !== garmentId),
    swaps: [
      ...amendment.swaps.filter((swap) => swap.garmentId !== garmentId),
      { garmentId, incoming },
    ],
  };
}

export function clearSwap(amendment: Amendment, garmentId: number): Amendment {
  return { ...amendment, swaps: amendment.swaps.filter((swap) => swap.garmentId !== garmentId) };
}

export function addToWearer(
  amendment: Amendment,
  wearerId: number,
  incoming: IncomingGarment,
): Amendment {
  return { ...amendment, additions: [...amendment.additions, { wearerId, incoming }] };
}

export function addToNewWearer(
  amendment: Amendment,
  key: string,
  incoming: IncomingGarment,
): Amendment {
  return {
    ...amendment,
    newWearers: amendment.newWearers.map((wearer) =>
      wearer.key === key ? { ...wearer, garments: [...wearer.garments, incoming] } : wearer,
    ),
  };
}

/** Drops an incoming garment wherever it was going. */
export function dropIncoming(amendment: Amendment, unitId: number): Amendment {
  return {
    ...amendment,
    additions: amendment.additions.filter((addition) => addition.incoming.unitId !== unitId),
    swaps: amendment.swaps.filter((swap) => swap.incoming.unitId !== unitId),
    newWearers: amendment.newWearers.map((wearer) => ({
      ...wearer,
      garments: wearer.garments.filter((garment) => garment.unitId !== unitId),
    })),
  };
}

export function addNewWearer(amendment: Amendment, name: string, relation = ''): Amendment {
  return {
    ...amendment,
    newWearers: [
      ...amendment.newWearers,
      { key: newWearerKey(), name, relation, mobile: '', garments: [] },
    ],
  };
}

export function removeNewWearer(amendment: Amendment, key: string): Amendment {
  return { ...amendment, newWearers: amendment.newWearers.filter((wearer) => wearer.key !== key) };
}

/**
 * Every unit this amendment has already spoken for, plus everything live on the booking.
 *
 * <p>
 * Both halves matter. The live ones are what the server refuses as a duplicate; the incoming ones
 * are what stops a clerk picking the same garment for two people in one amendment, which the server
 * would only catch on the second one.
 * </p>
 */
export function claimedUnitIds(booking: Booking, amendment: Amendment): Set<number> {
  const claimed = new Set<number>();

  for (const garment of liveGarments(booking)) {
    // A garment being dropped is not claimed — the whole point of dropping it is that it is free
    // again, and it must be pickable if the clerk changes their mind within the same amendment.
    if (!amendment.removals.includes(garment.id)) {
      claimed.add(garment.unitId);
    }
  }

  for (const swap of amendment.swaps) {
    claimed.add(swap.incoming.unitId);
  }

  for (const addition of amendment.additions) {
    claimed.add(addition.incoming.unitId);
  }

  for (const wearer of amendment.newWearers) {
    for (const garment of wearer.garments) {
      claimed.add(garment.unitId);
    }
  }

  return claimed;
}

/**
 * Just the units this amendment is bringing in — the subset of {@link claimedUnitIds} a clerk can
 * still change their mind about.
 *
 * <p>
 * The two are deliberately different sets, and the difference is what the rail needs to know. A
 * garment already live on the booking is claimed and draws with a tick, but un-picking it from the
 * rail would mean *removing it from the booking*, which is `toggleRemoval`'s job and is gated on the
 * garment's state — an issued one cannot be dropped at all. Offering that from a tile would either
 * do nothing or do something the rules forbid, so the rail is told which tiles are genuinely
 * reversible and leaves the rest alone.
 * </p>
 */
export function incomingUnitIds(amendment: Amendment): Set<number> {
  const incoming = new Set<number>();

  for (const swap of amendment.swaps) {
    incoming.add(swap.incoming.unitId);
  }

  for (const addition of amendment.additions) {
    incoming.add(addition.incoming.unitId);
  }

  for (const wearer of amendment.newWearers) {
    for (const garment of wearer.garments) {
      incoming.add(garment.unitId);
    }
  }

  return incoming;
}
/* --------------------------------------------------------------- the request */

/**
 * Turns the amendment into the operation list the endpoint takes.
 *
 * <p>
 * The order is deliberate: removals and swaps before additions, so a clerk who drops a garment and
 * re-adds the same one in a single amendment has the removal applied first. The other order collides
 * with the live row and is refused as a duplicate.
 * </p>
 */
export function toOperations(amendment: Amendment): AmendmentOperationInput[] {
  const operations: AmendmentOperationInput[] = [];

  for (const garmentId of amendment.removals) {
    operations.push({ kind: 'Remove', garmentId });
  }

  for (const swap of amendment.swaps) {
    operations.push({ kind: 'Swap', garmentId: swap.garmentId, unitId: swap.incoming.unitId });
  }

  for (const addition of amendment.additions) {
    operations.push({
      kind: 'Add',
      unitId: addition.incoming.unitId,
      wearerId: addition.wearerId,
    });
  }

  for (const wearer of amendment.newWearers) {
    operations.push({
      kind: 'AddWearer',
      newWearer: {
        name: wearer.name.trim(),
        relation: wearer.relation.trim() || null,
        mobile: wearer.mobile.trim() || null,
        garments: wearer.garments.map((garment) => ({ unitId: garment.unitId })),
      },
    });
  }

  return operations;
}

/**
 * The whole request — the operations, the reason trimmed, and whether the clerk went ahead past a
 * deposit shortfall. The website and the phone both send exactly this.
 */
export function amendmentRequest(amendment: Amendment): AmendBookingInput {
  return {
    operations: toOperations(amendment),
    reason: amendment.reason.trim(),
    acknowledgeDepositShortfall: amendment.acknowledgeShortfall,
  };
}

/** Prices one garment off the rail against the booking's existing charged period. */
export function incomingFrom(unit: AvailableUnit, days: number): IncomingGarment {
  return {
    unitId: unit.unitId,
    serialCode: unit.serialCode,
    ...(unit.referenceNumber ? { referenceNumber: unit.referenceNumber } : {}),
    itemId: unit.itemId,
    itemName: `${unit.itemName} · ${unit.variantDisplayName}`,
    rate: priceGarment(unit.rateCard, days)?.amount ?? null,
    rateCard: unit.rateCard,
  };
}

/* ------------------------------------------------------- the phone's words */

/**
 * The line over the phone's Apply button (`docs/Mobile_Redesign_Plan.md`, step 7): what is changing,
 * what the rental does, and the deposit — "1 swap · rental stays ₹9,800 · deposit unchanged".
 */
export function amendmentSummary(booking: Booking, amendment: Amendment, currency = ''): string {
  const money = amendmentMoney(booking, amendment);
  const added =
    amendment.additions.length +
    amendment.newWearers.reduce((sum, wearer) => sum + wearer.garments.length, 0);
  const counts = [
    amendment.swaps.length > 0
      ? `${amendment.swaps.length} ${amendment.swaps.length === 1 ? 'swap' : 'swaps'}`
      : '',
    amendment.removals.length > 0 ? `${amendment.removals.length} dropped` : '',
    added > 0 ? `${added} added` : '',
  ].filter(Boolean);

  if (counts.length === 0) {
    return 'Nothing changed yet';
  }

  const rental =
    money.rentalAfter === money.rentalBefore
      ? `rental stays ${currency}${formatAmount(money.rentalAfter)}`
      : `rental ${currency}${formatAmount(money.rentalBefore)} → ${currency}${formatAmount(money.rentalAfter)}`;
  const deposit =
    money.depositShortfall > 0
      ? `deposit ${currency}${formatAmount(money.depositShortfall)} short`
      : 'deposit unchanged';

  return [...counts, rental, deposit].join(' · ');
}

/** The button: the money the customer pays more, is owed, or that nothing changes. */
export function applyLabel(money: Pick<AmendmentMoney, 'payableDelta'>, currency = ''): string {
  if (money.payableDelta === 0) {
    return 'Apply · no money changes';
  }

  return money.payableDelta > 0
    ? `Apply · customer pays ${currency}${formatAmount(money.payableDelta)} more`
    : `Apply · customer is owed ${currency}${formatAmount(Math.abs(money.payableDelta))}`;
}

/** What a swap does to a garment's price, beside the garment coming in — "same price", "₹400 more". */
export function priceDifference(was: number | null, now: number | null, currency = ''): string {
  if (now === null) {
    return 'no rate';
  }

  const difference = now - (was ?? 0);

  if (difference === 0) {
    return 'same price';
  }

  return `${currency}${formatAmount(Math.abs(difference))} ${difference > 0 ? 'more' : 'less'}`;
}
